import { Box, FormControl, MenuItem, Select, Slider, Typography } from '@mui/material';
import { useState } from 'react';

import { type WallBoard, type WallShape } from '@walls/store';
import {
  getLineAttributes,
  getRectangleAttributes,
  getShapeType,
  getTextAttributes,
} from '../drawing/utils/shapeAttributes';
import { useShapeCommands } from '../shapes/hooks/useShapeCommands';
import { useShapePermissions } from '../shapes/hooks/useShapePermissions';
import { InlineMenuButton } from './InlineMenuButton';
import { LineWidthPanel } from './LineWidthPanel';
import { ShapeColorPanel } from './ShapeColorPanel';
import { inlineMenuSx, panelLabelSx } from './inlineMenuStyles';
import { stopMenuEvent, stopPointerEvent } from './menuEvents';
import { asSliderValue, toRgba } from './menuUtils';
import { fontOptions, fontSizes } from './shapeMenuOptions';

type ShapeMenuPanel = 'fillColor' | 'lineColor' | 'lineWidth' | 'textColor' | null;

interface ShapeInlineMenuProps {
  board: WallBoard;
  left: number;
  shape: WallShape;
  top: number;
}

export function ShapeInlineMenu({ board, left, shape, top }: ShapeInlineMenuProps) {
  const [activePanel, setActivePanel] = useState<ShapeMenuPanel>(null);
  const shapeCommands = useShapeCommands(board);
  const permissions = useShapePermissions(board, shape);
  const shapeType = getShapeType(shape);
  const isLine = shapeType === 'line';
  const isText = shapeType === 'text';
  const lineAttributes = isLine ? getLineAttributes(shape) : null;
  const textAttributes = isText ? getTextAttributes(shape) : null;
  const rectangleAttributes = !isLine && !isText ? getRectangleAttributes(shape) : null;

  const togglePanel = (panel: ShapeMenuPanel) => {
    setActivePanel((currentPanel) => (currentPanel === panel ? null : panel));
  };

  const updateAttributes = (attributesPatch: Record<string, unknown>) => {
    void shapeCommands.updateShapeAttributesAndPersist(shape, attributesPatch);
  };

  if (
    !permissions.canStyle &&
    !permissions.canLock &&
    !permissions.canDelete &&
    !permissions.canUnlock
  ) {
    return null;
  }

  return (
    <Box
      className="inline-menu"
      onClick={stopMenuEvent}
      onDoubleClick={stopMenuEvent}
      onPointerDown={stopPointerEvent}
      sx={{
        ...inlineMenuSx,
        left,
        top,
      }}
    >
      {!shape.isLocked ? (
        <>
          {permissions.canStyle && rectangleAttributes && (
            <>
              <Box sx={{ position: 'relative' }}>
                <InlineMenuButton label="Fill color" onClick={() => togglePanel('fillColor')}>
                  <Box
                    sx={{
                      bgcolor: toRgba(
                        rectangleAttributes.fillColor ?? '#FFFFFF',
                        rectangleAttributes.fillOpacity
                      ),
                      border: '1px solid #dddddd',
                      borderRadius: '50%',
                      height: 20,
                      width: 20,
                    }}
                  />
                </InlineMenuButton>
                {activePanel === 'fillColor' && (
                  <ShapeColorPanel
                    label="Fill color"
                    onChange={(fillColor) => updateAttributes({ fillColor })}
                    previewColor={toRgba(
                      rectangleAttributes.fillColor ?? '#FFFFFF',
                      rectangleAttributes.fillOpacity
                    )}
                    value={rectangleAttributes.fillColor ?? '#FFFFFF'}
                  >
                    <Typography sx={panelLabelSx}>Opacity</Typography>
                    <Slider
                      aria-label="Fill opacity"
                      max={100}
                      min={0}
                      onChange={(_, value) =>
                        updateAttributes({ fillOpacity: asSliderValue(value) })
                      }
                      size="small"
                      value={rectangleAttributes.fillOpacity}
                    />
                  </ShapeColorPanel>
                )}
              </Box>

              <Box sx={{ position: 'relative' }}>
                <InlineMenuButton label="Border color" onClick={() => togglePanel('lineColor')}>
                  <Box
                    sx={{
                      border: `4px solid ${rectangleAttributes.lineColor ?? '#000000'}`,
                      height: 17,
                      width: 17,
                    }}
                  />
                </InlineMenuButton>
                {activePanel === 'lineColor' && (
                  <ShapeColorPanel
                    label="Border color"
                    onChange={(lineColor) => updateAttributes({ lineColor })}
                    previewColor={rectangleAttributes.lineColor ?? '#000000'}
                    value={rectangleAttributes.lineColor ?? '#000000'}
                  />
                )}
              </Box>

              <Box sx={{ position: 'relative' }}>
                <InlineMenuButton label="Border width" onClick={() => togglePanel('lineWidth')}>
                  {lineWidthIcon}
                </InlineMenuButton>
                {activePanel === 'lineWidth' && (
                  <LineWidthPanel
                    label="Border width"
                    onChange={(lineThickness) => updateAttributes({ lineThickness })}
                    value={rectangleAttributes.lineThickness}
                  />
                )}
              </Box>
            </>
          )}

          {permissions.canStyle && lineAttributes && (
            <>
              <Box sx={{ position: 'relative' }}>
                <InlineMenuButton label="Line color" onClick={() => togglePanel('lineColor')}>
                  <Box
                    sx={{
                      bgcolor: lineAttributes.lineColor ?? '#000000',
                      border: '1px solid #dddddd',
                      borderRadius: '50%',
                      height: 20,
                      width: 20,
                    }}
                  />
                </InlineMenuButton>
                {activePanel === 'lineColor' && (
                  <ShapeColorPanel
                    label="Line color"
                    onChange={(lineColor) => updateAttributes({ lineColor })}
                    previewColor={lineAttributes.lineColor ?? '#000000'}
                    value={lineAttributes.lineColor ?? '#000000'}
                  />
                )}
              </Box>

              <Box sx={{ position: 'relative' }}>
                <InlineMenuButton label="Line width" onClick={() => togglePanel('lineWidth')}>
                  {lineWidthIcon}
                </InlineMenuButton>
                {activePanel === 'lineWidth' && (
                  <LineWidthPanel
                    label="Line width"
                    onChange={(lineThickness) => updateAttributes({ lineThickness })}
                    value={lineAttributes.lineThickness}
                  />
                )}
              </Box>
            </>
          )}

          {permissions.canStyle && textAttributes && (
            <>
              <Box sx={{ position: 'relative' }}>
                <InlineMenuButton label="Text color" onClick={() => togglePanel('textColor')}>
                  <Box
                    sx={{
                      bgcolor: textAttributes.color,
                      border: '1px solid #dddddd',
                      borderRadius: '50%',
                      height: 20,
                      width: 20,
                    }}
                  />
                </InlineMenuButton>
                {activePanel === 'textColor' && (
                  <ShapeColorPanel
                    label="Text color"
                    onChange={(color) => updateAttributes({ color })}
                    previewColor={textAttributes.color}
                    value={textAttributes.color}
                  />
                )}
              </Box>

              <Box sx={{ borderLeft: '2px solid #f3f5f8', display: 'flex', gap: 0.5, px: 0.75 }}>
                <FormControl size="small" sx={{ minWidth: 134 }}>
                  <Select
                    aria-label="Font family"
                    value={textAttributes.fontFamily}
                    onChange={(event) => updateAttributes({ fontFamily: event.target.value })}
                    sx={{ fontSize: 13, height: 30 }}
                  >
                    {fontOptions.map((font) => (
                      <MenuItem key={font.value} value={font.value} sx={{ fontFamily: font.value }}>
                        {font.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ width: 70 }}>
                  <Select
                    aria-label="Font size"
                    value={textAttributes.fontSize}
                    onChange={(event) => updateAttributes({ fontSize: Number(event.target.value) })}
                    sx={{ fontSize: 13, height: 30 }}
                  >
                    {fontSizes.map((fontSize) => (
                      <MenuItem key={fontSize} value={fontSize}>
                        {fontSize}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </>
          )}

          {permissions.canStyle && (
            <InlineMenuButton
              label="Duplicate"
              separatedStart={true}
              onClick={() => void shapeCommands.duplicateShape(shape)}
            >
              {duplicateIcon}
            </InlineMenuButton>
          )}

          {permissions.canLock && (
            <InlineMenuButton
              label="Lock"
              separatedStart={!permissions.canStyle}
              onClick={() => void shapeCommands.lockShape(shape.id)}
            >
              {lockIcon}
            </InlineMenuButton>
          )}

          {permissions.canDelete && (
            <InlineMenuButton
              label="Delete"
              separatedStart={!permissions.canLock}
              onClick={() => void shapeCommands.deleteShape(shape.id)}
            >
              {deleteIcon}
            </InlineMenuButton>
          )}
        </>
      ) : (
        permissions.canUnlock && (
          <InlineMenuButton label="Unlock" onClick={() => void shapeCommands.unlockShape(shape.id)}>
            {unlockIcon}
          </InlineMenuButton>
        )
      )}
    </Box>
  );
}

const lineWidthIcon = (
  <svg height="22" width="20" viewBox="0 0 24 24">
    <path d="M3 17h18v-2H3zm0 3h18v-1H3zm0-7h18v-3H3zm0-9v4h18V4z"></path>
  </svg>
);

const duplicateIcon = (
  <svg height="20" viewBox="0 0 24 24">
    <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2m0 16H8V7h11z"></path>
  </svg>
);

const lockIcon = (
  <svg height="20" viewBox="0 0 24 24">
    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2m-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2m3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1z"></path>
  </svg>
);

const unlockIcon = (
  <svg height="20" viewBox="0 0 24 24">
    <path d="M12 17c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2m6-9h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6h1.9c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2m0 12H6V10h12z"></path>
  </svg>
);

const deleteIcon = (
  <svg height="20" viewBox="0 0 20 22" style={{ fill: 'none', stroke: '#000000', strokeWidth: 2 }}>
    <g transform="translate(-2 -1)">
      <path d="M3,6H21"></path>
      <path d="M19,6V20a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6M8,6V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6"></path>
    </g>
  </svg>
);
